// ============================================
// DOSE LOGS
// ============================================

import { supabase } from '../../lib/supabase';
import type { DoseLog } from '../types';
import { requireUserId, throwWithContext, notify } from './_shared';
import { trackEvent } from '../../utils/analytics';

/* eslint-disable @typescript-eslint/no-explicit-any */

// inventory_id MUST stay in this list: mapDoseLogFromDb depends on it for
// per-vial history matching (InventoryPage.getRecentLogs). It was missing
// until 2026-07-03, which silently forced every vial onto the imprecise
// componentId fallback and re-introduced cross-vial history bleed after a
// refill (see docs/overnight-hardening-2026-07-03-report.md).
const DOSE_LOG_COLUMNS = 'id, user_id, protocol_id, component_id, phase_id, inventory_id, peptide_id, peptide_name, dose_mcg, units, injection_site, scheduled_time, logged_time, notes, skipped, phase_reflection, administration_id, created_at' as const;

export async function fetchDoseLogs(): Promise<DoseLog[]> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('dose_logs')
    .select(DOSE_LOG_COLUMNS)
    .eq('user_id', userId)
    .order('logged_time', { ascending: false })
    .limit(200);
  if (error) throwWithContext("Couldn't load your dose history", error);
  return (data || []).map(mapDoseLogFromDb);
}

export async function fetchRecentDoseLogs(days: number = 30): Promise<DoseLog[]> {
  const userId = await requireUserId();
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);
  const { data, error } = await supabase
    .from('dose_logs')
    .select(DOSE_LOG_COLUMNS)
    .eq('user_id', userId)
    .gte('logged_time', cutoffDate.toISOString())
    .order('logged_time', { ascending: false });
  if (error) throwWithContext("Couldn't load your recent dose history", error);
  return (data || []).map(mapDoseLogFromDb);
}

export async function fetchDoseLogsForComponent(componentId: string): Promise<DoseLog[]> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('dose_logs')
    .select(DOSE_LOG_COLUMNS)
    .eq('user_id', userId)
    .eq('component_id', componentId)
    .order('logged_time', { ascending: true });
  if (error) throwWithContext("Couldn't load dose logs", error);
  return (data || []).map(mapDoseLogFromDb);
}

export async function fetchTodayDoseLogs(): Promise<DoseLog[]> {
  const userId = await requireUserId();
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const { data, error } = await supabase
    .from('dose_logs')
    .select(DOSE_LOG_COLUMNS)
    .eq('user_id', userId)
    .gte('logged_time', todayStart.toISOString())
    .order('logged_time', { ascending: false });
  if (error) throwWithContext("Couldn't load today's dose logs", error);
  return (data || []).map(mapDoseLogFromDb);
}

export async function fetchLastDoseForPeptide(peptideId: string): Promise<DoseLog | null> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('dose_logs')
    .select(DOSE_LOG_COLUMNS)
    .eq('user_id', userId)
    .eq('peptide_id', peptideId)
    .order('logged_time', { ascending: false })
    .limit(1)
    .single();
  if (error || !data) return null;
  return mapDoseLogFromDb(data);
}

export async function addDoseLog(log: DoseLog): Promise<void> {
  const userId = await requireUserId();
  const row = mapDoseLogToDb(log, userId);
  const { error } = await supabase.from('dose_logs').insert(row);
  if (error) throwWithContext("Couldn't save your dose log", error);
  notify('dose_logs');
  // dose_logged: an actual dose saved (skips are not "doses logged"). No PII.
  if (!log.skipped) void trackEvent('dose_logged', { grouped: false });
}

/**
 * Insert several dose logs in ONE PostgREST call (one SQL statement,
 * so all rows land or none do). v3 group skips use this: skipping a
 * combined-vial administration writes one skipped row per component,
 * all sharing one administrationId. No inventory write (skips never
 * consume doses) - use logGroupDoseWithInventory for real doses.
 */
export async function addDoseLogs(logs: DoseLog[]): Promise<void> {
  if (logs.length === 0) return;
  const userId = await requireUserId();
  const rows = logs.map((log) => mapDoseLogToDb(log, userId));
  const { error } = await supabase.from('dose_logs').insert(rows);
  if (error) throwWithContext("Couldn't save your dose logs", error);
  notify('dose_logs');
}

/**
 * Atomically log a dose AND decrement the linked vial's dose counts.
 *
 * Wraps both writes in a single Postgres transaction via the
 * log_dose_with_inventory RPC (see migration
 * 20260423_log_dose_with_inventory.sql). This prevents the data drift
 * described in P1-03 / C-04 where a failed inventory update would
 * leave a dose logged but the vial not decremented. Also holds a row
 * lock on the inventory row for the duration of the transaction, so
 * two concurrent logs against the same vial can't both observe the
 * pre-decrement value and lose one decrement.
 *
 * Pass `inventoryId = null` when the dose is off-protocol or the
 * linked vial is no longer active; the function still records the
 * dose log but skips the inventory write.
 */
export async function logDoseWithInventory(
  log: DoseLog,
  inventoryId: string | null,
  dosesConsumed: number,
): Promise<{ inventoryDosesRemaining: number | null }> {
  // requireUserId ensures we have a session; the RPC itself also checks
  // auth.uid() on the server side.
  await requireUserId();
  const { data, error } = await supabase.rpc('log_dose_with_inventory', {
    p_log_id: log.id,
    p_protocol_id: log.protocolId,
    p_component_id: log.componentId,
    p_phase_id: log.phaseId,
    p_peptide_id: log.peptideId,
    p_peptide_name: log.peptideName,
    p_dose_mcg: log.doseMcg,
    p_units: log.units,
    p_injection_site: log.injectionSite,
    p_scheduled_time: log.scheduledTime ?? null,
    p_logged_time: log.loggedTime,
    p_notes: log.notes ?? null,
    p_skipped: log.skipped,
    p_created_at: log.createdAt,
    p_inventory_id: inventoryId,
    p_doses_consumed: dosesConsumed,
  });
  if (error) throwWithContext("Couldn't save your dose log", error);
  notify('dose_logs');
  if (inventoryId) notify('inventory');
  // dose_logged: single real dose saved via the atomic RPC. No PII.
  if (!log.skipped) void trackEvent('dose_logged', { grouped: false });
  // RPC returns the canonical post-decrement doses_remaining from
  // inside its transaction. NULL when no inventory write happened
  // (off-protocol, archived vial, no linked inventory). Callers use
  // this — not a pre-RPC snapshot — to decide if the vial is empty,
  // because a pre-RPC snapshot races with concurrent writes from
  // other devices. See 20260427_phase3_data_integrity_p0_round2.sql.
  return { inventoryDosesRemaining: typeof data === 'number' ? data : null };
}

/**
 * Atomically log a combined-vial administration: N per-component dose
 * logs sharing one administrationId + ONE decrement of the shared
 * vial, in one transaction via the log_group_dose_with_inventory RPC
 * (migration 20260702_add_protocol_v3_phase_a_group_identity.sql).
 *
 * Contract notes (E3/E7):
 * - Every log MUST carry the same administrationId; the RPC rejects
 *   missing ones loudly.
 * - p_doses_consumed decrements ONCE for the whole group regardless
 *   of how many logs are in the array.
 * - NO auto-backfill: a vial with null totals records the logs and
 *   skips the decrement.
 * - Pass inventoryId = null to record logs with no inventory write.
 */
export async function logGroupDoseWithInventory(
  logs: DoseLog[],
  inventoryId: string | null,
  dosesConsumed: number,
): Promise<{ inventoryDosesRemaining: number | null }> {
  if (logs.length === 0) {
    throw new Error('logGroupDoseWithInventory requires at least one log');
  }
  await requireUserId();
  const { data, error } = await supabase.rpc('log_group_dose_with_inventory', {
    p_logs: logs.map((log) => ({
      id: log.id,
      protocol_id: log.protocolId,
      component_id: log.componentId,
      phase_id: log.phaseId,
      peptide_id: log.peptideId,
      peptide_name: log.peptideName,
      dose_mcg: log.doseMcg,
      units: log.units,
      injection_site: log.injectionSite,
      scheduled_time: log.scheduledTime ?? null,
      logged_time: log.loggedTime,
      notes: log.notes ?? null,
      skipped: log.skipped,
      created_at: log.createdAt,
      administration_id: log.administrationId ?? null,
    })),
    p_inventory_id: inventoryId,
    p_doses_consumed: dosesConsumed,
  });
  if (error) throwWithContext("Couldn't save your dose log", error);
  notify('dose_logs');
  if (inventoryId) notify('inventory');
  // dose_logged: one combined-vial administration = ONE event (not one per
  // component). Skip if the whole group was a skip. No PII.
  if (logs.some((l) => !l.skipped)) void trackEvent('dose_logged', { grouped: true });
  return { inventoryDosesRemaining: typeof data === 'number' ? data : null };
}

export async function updateDoseLog(id: string, updates: Partial<DoseLog>): Promise<void> {
  const userId = await requireUserId();
  const dbUpdates: Record<string, unknown> = {};
  if (updates.phaseReflection !== undefined) dbUpdates.phase_reflection = updates.phaseReflection;
  if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
  if (updates.skipped !== undefined) dbUpdates.skipped = updates.skipped;

  const { error } = await supabase
    .from('dose_logs')
    .update(dbUpdates)
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't update dose log", error);
  notify('dose_logs');
}

/**
 * Delete every row of one administration (the Undo path for skips).
 * A combined-vial skip writes N rows sharing one administrationId, so
 * undo must remove the whole set, not a single row. Safe for skips
 * only: skip rows never touch inventory, so deleting them fully
 * reverses the action with no counter to restore.
 */
export async function deleteDoseLogsByAdministration(administrationId: string): Promise<void> {
  const userId = await requireUserId();
  const { error } = await supabase
    .from('dose_logs')
    .delete()
    .eq('administration_id', administrationId)
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't undo that", error);
  notify('dose_logs');
}

function mapDoseLogFromDb(row: any): DoseLog {
  // Postgres numeric columns (dose_mcg, units) serialize as strings via
  // PostgREST to preserve arbitrary precision. Without Number() coercion
  // downstream arithmetic mostly works via JS implicit coercion, but
  // truthiness (`||`) and lexicographic comparisons (`"9" > "1000"`)
  // misfire. See P1-07 / H-04 in the data-integrity audit.
  return {
    id: row.id,
    // protocolId/componentId/phaseId are nullable since 2026-04-29 (ad-hoc
    // dose logs leave them null). Coerce DB null → JS null for clarity.
    protocolId: row.protocol_id ?? null,
    componentId: row.component_id ?? null,
    phaseId: row.phase_id ?? null,
    inventoryId: row.inventory_id ?? null,
    peptideId: row.peptide_id,
    peptideName: row.peptide_name,
    doseMcg: Number(row.dose_mcg),
    units: Number(row.units),
    injectionSite: row.injection_site,
    scheduledTime: row.scheduled_time ?? undefined,
    loggedTime: row.logged_time,
    notes: row.notes ?? undefined,
    skipped: row.skipped ?? false,
    phaseReflection: row.phase_reflection ?? undefined,
    administrationId: row.administration_id ?? null,
    createdAt: row.created_at,
  };
}

function mapDoseLogToDb(log: DoseLog, userId?: string): Record<string, unknown> {
  const row: Record<string, unknown> = {
    id: log.id,
    protocol_id: log.protocolId,
    component_id: log.componentId,
    phase_id: log.phaseId,
    inventory_id: log.inventoryId ?? null,
    peptide_id: log.peptideId,
    peptide_name: log.peptideName,
    dose_mcg: log.doseMcg,
    units: log.units,
    injection_site: log.injectionSite,
    scheduled_time: log.scheduledTime ?? null,
    logged_time: log.loggedTime,
    notes: log.notes ?? null,
    skipped: log.skipped,
    phase_reflection: log.phaseReflection ?? null,
    administration_id: log.administrationId ?? null,
    created_at: log.createdAt,
  };
  if (userId) row.user_id = userId;
  return row;
}
